<template>
  <validation-observer ref="observer">
    <form role="form" @submit.prevent="transferGoals">
      <modal :show.sync="show" size="lg" @close="onCancel">
        <template slot="header">
          <div class="row">
            <h6 class="modal-title col-12 ml-3 mb--3">
              Transferir Metas
            </h6>
          </div>
        </template>
    
        <div class="mb-3">
          <label class="form-control-label">Ano Safra</label>
          <base-input disabled :value="anosafra.descricao" />
        </div>

        <div class="row mb-3">
          <div :class="`col-${transferAction == 'transfer' ? 6 : 12}`">
            <label class="form-control-label">Ação de Transferência de Metas</label>
            <base-radio name="copy" v-model="transferAction">
              Copiar
            </base-radio>
            <base-radio name="transfer" v-model="transferAction">
              Transferir
            </base-radio>
          </div>

          <div class="col-6" v-if="transferAction == 'transfer'">
            <label class="form-control-label">Tipo de Transferência</label>
            <base-radio name="replace" v-model="transferType">
              Substituir
            </base-radio>
            <base-radio name="sum" v-model="transferType">
              Somar
            </base-radio>
          </div>
        </div>

        <div v-if="showWalletTransferOptions" class="row mb-3">
          <div class="col-12 mb-3">
            <label class="form-control-label">Transferir Carteira de Clientes</label>
            <base-switch v-model="walletTransfer"/>
          </div>
          <div class="col-12" v-show="walletTransfer">
            <label class="form-control-label">Ação de Transferência de Carteira</label>
            <base-radio name="replace" v-model="walletTransferAction">
              Substituir
            </base-radio>
            <base-radio name="sum" v-model="walletTransferAction">
              Somar
            </base-radio>
          </div>
        </div>
    
        <div class="transfer-fields-container">
          <div class="transfer-column">
            <user-multiselect
              v-model="originSalesman"
              name-only
              @input="onOriginSalesmanChange" 
              :user-type="'salesman'"
              placeholder="Escolha o vendedor origem" 
              :label="'Vendedor Origem'" 
              :filter="filter" 
            />
            <div v-if="originSalesman && emptyOriginSalesmanGoals">
              <card>
                <div>
                  <span>O vendedor não possui metas cadastradas no anosafra vigente. Por favor selecione outro vendedor!</span>
                </div>
              </card>
            </div>  
            <div v-else-if="originSalesman && originSalesmanGoals">
              <br>
              <total-values-card
                :total-values="originSalesmanGoals" 
                :loading="loadingOriginSalesmanGoals"
              />
            </div>
          </div>
    
          <div class="transfer-column">
            <user-multiselect
              v-model="destinySalesman"
              name-only
              @input="onDestinySalesmanChange"
              :user-type="'salesman'" 
              placeholder="Escolha o vendedor destino"
              :label="'Vendedor Destino'" 
              :filter="filter" 
            />
            <div v-if="destinySalesman && destinySalesman">
              <br>
              <total-values-card 
                :total-values="destinySalesmanGoals" 
                :loading="loadingDestinySalesmanGoals"
              />
            </div>
          </div>
        </div>
    
        <template slot="footer">
          <base-button type="secondary" @click="onCancel">
            Cancelar
          </base-button>
          <el-tooltip
            placement="bottom"
            :content="tooltipText" 
            :disabled="!disabledTransfer"
          >
            <base-button 
              type="success" 
              :loading="loadingTransfer"
              :disabled="disabledTransfer"
              @click="transferGoals"
            >
              <span>
                Continuar
              </span>
            </base-button>
          </el-tooltip>
        </template>
      </modal>
    </form>
  </validation-observer>
</template>

<script setup>
import UserMultiselect from '@/components/Multiselect/user_multiselect.vue';
import store from '@/store';
import TotalValuesCard from './TotalValuesCard.vue';
import axios from '@/backend/axios/index';
import swal from "sweetalert2";
import { ref, computed, defineProps, defineEmits, getCurrentInstance, onMounted } from 'vue';

const props = defineProps(['show']);
const emit  = defineEmits(['update:show']);

const show = computed({
  get: () => props.show,
  set: (value) => emit('update:show', value),
})

const anosafra = computed(() => store.state.currentAnosafra);
const filter = computed(() => ({ anosafra: anosafra.value, is_salesman: true }));
const originSalesman = ref(null);
const destinySalesman = ref(null);
const originSalesmanGoals = ref();
const destinySalesmanGoals = ref(null);
const loadingDestinySalesmanGoals = ref(false)
const loadingOriginSalesmanGoals = ref(false)
const loadingTransfer = ref(false)
const transferAction  = ref("copy")
const transferType    = ref("replace")
const walletTransferAction  = ref("replace")
const walletTransfer = ref(false)

let toasted = null;

const emptyOriginSalesmanGoals = computed(() => {
  if (!originSalesmanGoals.value) return true;

  return !Object.values(originSalesmanGoals.value).some((value) => { return value > 0})
})

const disabledTransfer = computed(() => {
  if (!originSalesman.value || !destinySalesman.value)               return true;
  if (originSalesman.value.id == destinySalesman.value.id)           return true;
  if (emptyOriginSalesmanGoals.value || salesmanTypeDivergent.value) return true;
  
  return false
})

const tooltipText = computed(() => {
  if (!originSalesman.value || !destinySalesman.value) {
    return `Ação não permitida: Vendedor não selecionado. Por favor, selecione vendedor de origem e de destino.`
  }

  if (originSalesman.value.id == destinySalesman.value.id) {
    return `Ação não permitida: Vendedor destino deve ser diferente do origem.`
  }

  if (emptyOriginSalesmanGoals.value) {
    return `Ação não permitida: O vendedor selecionado como 'Vendedor Origem' não possui metas cadastradas 
      para o ano safra vigente. Por favor, escolha outro vendedor.`
  }

  if (salesmanTypeDivergent.value) {
    return `Ação não permitida: Tipo do Vendedor Destino diverge do tipo do Vendedor Origem.`
  }
});

const transferGoals = async () => {
  try {
    const confirmationResult = await swal.fire({
      title: "Tem certeza?",
      text: "Tem certeza de que deseja continuar? As alterações feitas nas metas do vendedor de origem e do vendedor de destino serão permanentes e não poderão ser desfeitas",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, transferir!",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
    });

    if (!confirmationResult.isConfirmed) return;

    loadingTransfer.value = true

    const params = {
      origin_user_id: originSalesman.value.id,
      destination_user_id: destinySalesman.value.id,
      anosafra_id: anosafra.value.id,
      transfer_action: transferAction.value
    }

    if (transferAction.value == 'transfer') params.transfer_type = transferType.value;
    if (showWalletTransferOptions.value && walletTransfer.value) params.wallet_transfer_action = walletTransferAction.value;

    const response = await axios.patch("/api/v1/goals/transfer", params);

    toasted.global.defaultSuccess({ msg: "Operação realizada com sucesso!" });
    onCancel()

    return response.data
  } catch (error) {
    onCancel()
    toasted.global.defaultError({ msg: "Não foi possível concluir a ação, por favor entre em contato com o suporte." });
  } finally {
    loadingTransfer.value = false
  }
};

const getTotalValues = async (salesmanId) => {
  if (!salesmanId) return;

  try {
    const response = await axios.get("/api/v1/totalvalues", {
      params: {
        salesman_goals_only: true,
        salesman_id: salesmanId,
        anosafra_id: anosafra.value.id
      },
    });

    return response.data;
  } catch (error) {
    console.warn("[GoalsTransferModal][getTotalValues] Error", error);
  }
}

onMounted(() => {
  const currentInstance = getCurrentInstance()
  toasted               = currentInstance.proxy.$toasted;
});

const salesmanTypeDivergent = computed(() => {
  if (!originSalesman.value || !destinySalesman.value)                              return false;
  if (!originSalesman.value.salesman_type || !destinySalesman.value.salesman_type)  return true;

  return (originSalesman.value.salesman_type != destinySalesman.value.salesman_type);
})

const showWalletTransferOptions = computed(() => {
  if (!originSalesman.value) return false;
  if (!salesmanTypeDivergent.value && originSalesman.value.salesman_type == 'campo') return true;

  return false;
})

const onOriginSalesmanChange = async (value) => {
  if (!value) return;

  loadingOriginSalesmanGoals.value = true
  originSalesmanGoals.value = await getTotalValues(value.id);
  loadingOriginSalesmanGoals.value = false
};

const onDestinySalesmanChange = async (value) => {
  if (!value) return;

  loadingDestinySalesmanGoals.value = true
  destinySalesmanGoals.value = await getTotalValues(value.id);
  loadingDestinySalesmanGoals.value = false
};

const onCancel = () => {
  show.value = false;
  resetInputs()
};

const resetInputs = () => {
  originSalesman.value = null;
  destinySalesman.value = null;
  transferType.value = 'replace';
  transferAction.value = 'copy';
}

</script>
<style>
.transfer-fields-container {
  min-height: 300px;
}
</style>
